import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { characterAPI } from '../../../services/api';

export const fetchCharacters = createAsyncThunk(
  'characters/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      return await characterAPI.getCharacters();
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || err.message);
    }
  }
);

export const fetchSlotPrice = createAsyncThunk(
  'characters/fetchSlotPrice',
  async (_, { rejectWithValue }) => {
    try {
      return await characterAPI.getSlotPrice();
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || err.message);
    }
  }
);

export const purchaseCharacterSlot = createAsyncThunk(
  'characters/purchaseSlot',
  async (_, { rejectWithValue }) => {
    try {
      return await characterAPI.purchaseSlot();
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || err.message);
    }
  }
);

export const equipCharacterModule = createAsyncThunk(
  'characters/equipModule',
  async ({ characterId, moduleId, rank = 0 }, { rejectWithValue }) => {
    try {
      return await characterAPI.equipModule(characterId, moduleId, rank);
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || err.message);
    }
  }
);

// Takes a part off a not-yet-completed character (it returns to inventory)
export const unequipCharacterModule = createAsyncThunk(
  'characters/unequipModule',
  async ({ characterId, slotType }, { rejectWithValue }) => {
    try {
      return await characterAPI.unequipModule(characterId, slotType);
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || err.message);
    }
  }
);

// Locks the assembly — after this parts can't be changed, the hero can go on missions
export const completeCharacter = createAsyncThunk(
  'characters/complete',
  async (characterId, { rejectWithValue }) => {
    try {
      return await characterAPI.completeCharacter(characterId);
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || err.message);
    }
  }
);

const replaceCharacter = (state, character) => {
  const idx = state.list.findIndex((c) => c.id === character.id);
  if (idx !== -1) state.list[idx] = character;
};

const characterSlice = createSlice({
  name: 'characters',
  initialState: {
    list: [],
    slotPrice: null, // price of the next hero slot from the server; null until loaded
    isLoading: false,
    isPurchasing: false,
    isEquipping: false,
    error: null,
  },
  reducers: {
    clearError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCharacters.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCharacters.fulfilled, (state, action) => {
        state.isLoading = false;
        state.list = action.payload;
      })
      .addCase(fetchCharacters.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchSlotPrice.fulfilled, (state, action) => {
        state.slotPrice = action.payload.price;
      })
      .addCase(purchaseCharacterSlot.pending, (state) => {
        state.isPurchasing = true;
        state.error = null;
      })
      .addCase(purchaseCharacterSlot.fulfilled, (state, action) => {
        state.isPurchasing = false;
        state.list = [action.payload, ...state.list];
      })
      .addCase(purchaseCharacterSlot.rejected, (state, action) => {
        state.isPurchasing = false;
        state.error = action.payload;
      })
      .addCase(equipCharacterModule.pending, (state) => {
        state.isEquipping = true;
        state.error = null;
      })
      .addCase(equipCharacterModule.fulfilled, (state, action) => {
        state.isEquipping = false;
        replaceCharacter(state, action.payload);
      })
      .addCase(equipCharacterModule.rejected, (state, action) => {
        state.isEquipping = false;
        state.error = action.payload;
      })
      .addCase(unequipCharacterModule.pending, (state) => {
        state.isEquipping = true;
        state.error = null;
      })
      .addCase(unequipCharacterModule.fulfilled, (state, action) => {
        state.isEquipping = false;
        replaceCharacter(state, action.payload);
      })
      .addCase(unequipCharacterModule.rejected, (state, action) => {
        state.isEquipping = false;
        state.error = action.payload;
      })
      .addCase(completeCharacter.pending, (state) => {
        state.isEquipping = true;
        state.error = null;
      })
      .addCase(completeCharacter.fulfilled, (state, action) => {
        state.isEquipping = false;
        replaceCharacter(state, action.payload);
      })
      .addCase(completeCharacter.rejected, (state, action) => {
        state.isEquipping = false;
        state.error = action.payload;
      });
  },
});

export const { clearError } = characterSlice.actions;
export default characterSlice.reducer;
